/**
 * ContactFocusView.jsx — Iter 168 (Danny)
 *
 * Customer Focus view, mirroring the Lead Focus layout pixel-for-pixel:
 *
 *   ┌─ CustomerHero ──────────────────────────────────────────┐
 *   │  Logo · Name · stage strip (implementation tracks) ·     │
 *   │  Communications · AI assist · Quick note · KPIs          │
 *   └──────────────────────────────────────────────────────────┘
 *   ┌── 320px ──────┬── center (tabs) ──┬── 340px ─────────────┐
 *   │ CustomerVitals│ Overview, Implem- │ CustomerAIUpsellPanel│
 *   │ Practice,     │ entation, Activity│ (replaces AI         │
 *   │ Contact,      │ Enrollments,      │ Targeting)           │
 *   │ Enrollments,  │ Notes             │                      │
 *   │ Implementation│                   │                      │
 *   └───────────────┴───────────────────┴──────────────────────┘
 *
 * Data source: /api/contacts/{id} + /api/customers/{id}/bucket +
 * /api/contacts/{id}/enrollments + /api/leads (filtered by contact_id).
 *
 * Stage advancement (admin only) hits PUT /api/customers/{id}/implementation.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, FileText, ArrowLeft } from "lucide-react";
import { makeRCCall } from "@/lib/rcIntegrationApi";
import { getLeadScore } from "@/lib/getLeadScore";

import CustomerHero from "@/components/CustomerHero";
import CustomerVitals from "@/components/CustomerVitals";
import CustomerAIUpsellPanel from "@/components/CustomerAIUpsellPanel";
import ImplementationStatusPanel from "@/components/ImplementationStatusPanel";
import ContactCard from "@/components/ContactCard";
import LeadMeetingsWidget from "@/components/LeadMeetingsWidget";
import AddEnrollmentModal from "@/components/AddEnrollmentModal";
import ChurnExitFormModal from "@/components/ChurnExitFormModal";

/** Iter 168 — same defensive helper as in CustomerVitals. FastAPI 422
 *  errors return a list of dict objects under `detail`; React can't
 *  render that. Always run errors through this picker. */
function _pickErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "implementation", label: "Implementation" },
  { id: "enrollments", label: "Enrollments" },
  { id: "deals", label: "Deals" },
  { id: "activity", label: "Activity" },
  { id: "notes", label: "Notes" },
];

function QuickNoteModal({ open, customerName, onClose, onSubmit }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  if (!open) return null;
  const submit = () => onSubmit?.(text);
  return (
    <div
      data-testid="customer-quick-note-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 540, boxShadow: "0 22px 60px -12px rgba(15,23,42,0.35)", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "16px 22px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
          <FileText style={{ width: 16, height: 16, color: "#92400E" }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Quick note</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{customerName ? `Adds to ${customerName}'s notes` : "Adds to this customer's notes"}</div>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <textarea
            data-testid="customer-quick-note-textarea"
            autoFocus value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            placeholder="Renewal coming up · Champion contact left · pricing concern raised…"
            style={{ width: "100%", minHeight: 110, border: "0.5px solid #e2e8f0", borderRadius: 8, padding: 12, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", lineHeight: 1.55 }} />
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6 }}>Cmd/Ctrl + Enter to save</div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "0.5px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} className="btn" style={{ background: "#fff", color: "#475569", border: "0.5px solid #e2e8f0" }}>Cancel</button>
          <button
            data-testid="customer-quick-note-save"
            onClick={submit}
            disabled={!text.trim()}
            className="btn btn-primary"
            style={{ opacity: text.trim() ? 1 : 0.5, cursor: text.trim() ? "pointer" : "not-allowed" }}>
            Save note
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContactFocusView() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const contactId = id || searchParams.get("id");
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = ["super_admin", "crm_admin", "sales_manager"].includes((user?.role || "").toLowerCase());

  const [contact, setContact] = useState(null);
  const [bucketData, setBucketData] = useState(null);
  const [enrollments, setEnrollments] = useState({ enrollments: [], catalog: {}, active_count: 0, active_total_monthly: 0, active_total_patients: 0 });
  const [leads, setLeads] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [addEnrollmentOpen, setAddEnrollmentOpen] = useState(false);
  // Iter 171.A — Churned / Exited Phase A state (Danny)
  const [churnModalOpen, setChurnModalOpen] = useState(false);

  // Per-customer tab persistence — same UX as LeadFocus.
  // Use `contactId` (which falls back to ?id= search-param) so the
  // bucket is consistent whether the user lands via /customers/:id
  // or /contact/:id?id=... .
  const [activeTab, setActiveTab] = useState(() => {
    try { return sessionStorage.getItem(`customer_tab_${contactId}`) || "overview"; } catch (_) { return "overview"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(`customer_tab_${contactId}`, activeTab); } catch (_) {}
  }, [activeTab, contactId]);

  const hdrs = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadAll = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const [contactRes, bucketRes, enrollRes, leadsRes] = await Promise.all([
        axios.get(`${API}/contacts/${contactId}`, { headers: hdrs }),
        axios.get(`${API}/customers/${contactId}/bucket`, { headers: hdrs }).catch(() => ({ data: null })),
        axios.get(`${API}/contacts/${contactId}/enrollments`, { headers: hdrs }).catch(() => ({ data: { enrollments: [], catalog: {} } })),
        axios.get(`${API}/leads`, { headers: hdrs }).catch(() => ({ data: [] })),
      ]);
      setContact(contactRes.data || null);
      setNotes(contactRes.data?.notes || "");
      setBucketData(bucketRes.data || null);
      setEnrollments(enrollRes.data || { enrollments: [], catalog: {} });
      // STRICT match: only leads explicitly linked via contact_id (the new
      // Customers overhaul stamps this at Closed Won). No fuzzy fallback.
      setLeads((leadsRes.data || []).filter(l => l.contact_id === contactId));
    } catch (e) {
      console.warn("[ContactFocusView] load failed", e?.response?.data?.detail || e?.message);
    } finally {
      setLoading(false);
    }
  }, [contactId, hdrs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Lazy-load assignable specialists so we can resolve the specialist name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/dialer/assignable-users`, { headers: hdrs });
        if (!cancelled) setSpecialists(r.data || []);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [hdrs]);

  const advanceStage = async (trackKey, stageId) => {
    if (!isAdmin) return;
    try {
      await axios.put(`${API}/customers/${contactId}/implementation`, { [trackKey]: stageId }, { headers: hdrs });
      toast.success("Implementation updated");
      loadAll();
    } catch (e) {
      toast.error(_pickErrorMessage(e, "Couldn't update implementation"));
    }
  };

  const saveNotes = async () => {
    try {
      await axios.put(`${API}/contacts/${contactId}`, { notes }, { headers: hdrs });
      toast.success("Notes saved");
    } catch (e) { toast.error(_pickErrorMessage(e, "Failed to save")); }
  };

  const onComms = async (action) => {
    if (action === "call") {
      try { await makeRCCall({ to: contact?.phone }); }
      catch { toast.error("Couldn't initiate call"); }
    } else if (action === "email") {
      toast.info("Composing email — switch to the Email tab in Communications");
    } else if (action === "sms") {
      toast.info("SMS composer — coming soon");
    } else {
      toast.info(`Logged ${action}`);
    }
  };

  // NOTE: hooks must run on every render — these memos sit ABOVE the
  // early returns so we never violate React's Rules of Hooks.
  const currentProducts = useMemo(() => {
    const fromEnroll = (enrollments.enrollments || []).map(e => {
      const def = enrollments.catalog?.[e.product];
      return def?.label || e.product;
    });
    const fromLeads = leads.flatMap(l => l.product_interest || []);
    return [...new Set([...fromEnroll, ...fromLeads])];
  }, [enrollments, leads]);

  const painPoints = useMemo(() => [...new Set(leads.flatMap(l => l.pain_points || []))], [leads]);

  if (loading) {
    return (
      <div style={{ padding: 40, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Loader2 style={{ width: 24, height: 24, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
      </div>
    );
  }

  if (!contact) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p style={{ color: "var(--text-3)" }}>Customer not found</p>
        <button className="btn" onClick={() => navigate("/customers")} style={{ marginTop: 10 }}>
          <ArrowLeft style={{ width: 12, height: 12 }} /> Back to customers
        </button>
      </div>
    );
  }

  const specialistName = (() => {
    const sid = bucketData?.implementation?.specialist_user_id;
    if (!sid) return null;
    const u = specialists.find(s => s.id === sid);
    if (!u) return sid;
    return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
  })();

  const timeline = leads.flatMap(l => (l.activities || []).map(a => ({ ...a, leadTitle: l.title })))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <ImplementationStatusPanel customerId={contact.id} isAdmin={isAdmin} />
            {contact.source_lead_id && (
              <div style={{ padding: "12px 14px", borderRadius: 10, background: "var(--blue-soft)", border: "0.5px solid #cfe2ff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontSize: 12, color: "var(--text-2)" }}>Source lead — this customer was created from a Closed Won lead.</div>
                <button
                  data-testid="customer-source-lead-link"
                  onClick={() => navigate(`/lead-focus/${contact.source_lead_id}`)}
                  className="btn"
                  style={{ fontSize: 11, padding: "5px 12px" }}>
                  Open lead <ExternalLink style={{ width: 11, height: 11 }} />
                </button>
              </div>
            )}
            {painPoints.length > 0 && (
              <div style={{ padding: "16px 18px", background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 10 }}>
                <h3 style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--navy)" }}>Known challenges</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {painPoints.map((p, i) => (
                    <span key={i} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999, background: "#fef3c7", color: "#92400e", border: "0.5px solid #fde68a" }}>{p}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "implementation":
        return <ImplementationStatusPanel customerId={contact.id} isAdmin={isAdmin} />;

      case "enrollments":
        return (
          <ContactCard
            contactId={contact.id}
            leadContext={leads[0] ? {
              specialty: contact.specialty || leads[0]?.specialty || "",
              pain_points: painPoints,
              num_patients: leads[0]?.num_patients || contact?.num_patients || 0,
              company: contact.company,
            } : null}
          />
        );

      case "deals":
        return (
          <div style={{ padding: "16px 18px", background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 10 }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--navy)" }}>Associated deals</h3>
            {leads.length === 0 ? (
              <p data-testid="deals-empty" style={{ fontSize: 12, color: "var(--text-3)", padding: "12px 0" }}>
                No deals linked to this customer yet.
              </p>
            ) : leads.map(lead => (
              <div
                key={lead.id}
                onClick={() => navigate(`/lead-focus/${lead.id}`)}
                data-testid={`customer-deal-${lead.id}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "0.5px solid var(--border-soft)", cursor: "pointer" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 520, color: "var(--text)" }}>{lead.title || lead.company}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                    {(lead.stage || "").replace(/_/g, " ")} · ${(lead.value || 0).toLocaleString()} · AI {getLeadScore(lead)}
                  </div>
                </div>
                <ExternalLink style={{ width: 12, height: 12, color: "var(--text-3)" }} />
              </div>
            ))}
          </div>
        );

      case "activity":
        return (
          <div style={{ padding: "16px 18px", background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 10 }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--navy)" }}>Activity timeline</h3>
            {timeline.length === 0 ? (
              <p data-testid="activity-empty" style={{ fontSize: 12, color: "var(--text-3)", padding: "12px 0" }}>
                No activity yet for this customer.
              </p>
            ) : timeline.slice(0, 50).map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: t.type === "demo" ? "var(--purple, #7c3aed)" : t.type === "call" ? "var(--blue)" : t.type === "email" ? "var(--success)" : "#f59e0b" }} />
                <div>
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>{t.description}</div>
                  <div style={{ fontSize: 10, color: "var(--text-3)" }}>
                    {t.leadTitle && <span style={{ color: "var(--blue)" }}>{t.leadTitle} · </span>}{t.timestamp}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "notes":
        return (
          <div style={{ padding: "16px 18px", background: "var(--paper)", border: "0.5px solid var(--border-soft)", borderRadius: 10 }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--navy)" }}>Notes</h3>
            <textarea
              className="input"
              rows={12}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Add notes about this customer..."
              data-testid="customer-notes-textarea"
              style={{ fontSize: 12, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 9, color: "var(--text-3)", marginTop: 4 }}>Auto-saves on blur</div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid="contact-focus-view" style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
      <CustomerHero
        contact={contact}
        bucketData={bucketData}
        enrollmentSummary={enrollments}
        onBack={() => navigate("/customers")}
        onComms={onComms}
        onAiAssist={() => toast.info("AI assist drawer coming next iteration")}
        onQuickNote={() => setQuickNoteOpen(true)}
        onAddProduct={() => setAddEnrollmentOpen(true)}
        onAdvanceStage={advanceStage}
        canEditImpl={isAdmin}
        canManageChurn={isAdmin}
        onFlagForClosure={async () => {
          const reason = (window.prompt("Why is this customer being flagged for closure? (optional)") || "").trim();
          try {
            await axios.post(`${API}/customers/${contactId}/flag-for-closure`, { reason: reason || null }, { headers: hdrs });
            toast.success("Flagged for manager review");
            loadAll();
          } catch (e) {
            toast.error(_pickErrorMessage(e, "Couldn't flag for closure"));
          }
        }}
        onMoveToChurned={() => setChurnModalOpen(true)}
        onReEngage={() => {
          // Phase B will create a re-engagement lead. For now just navigate
          // the rep to the New Lead form pre-filled with the churned company.
          const name = encodeURIComponent(contact?.company || "");
          navigate(`/add-lead?prefill_company=${name}&re_engage_from=${contactId}`);
        }}
      />

      {/* 3-column rail — identical breakpoints to LeadFocus */}
      <div className="customer-focus-rails" style={{ display: "grid", gridTemplateColumns: "320px 1fr 340px", gap: 18, alignItems: "flex-start", marginTop: 18 }}>
        <CustomerVitals
          contact={contact}
          enrollments={enrollments.enrollments}
          catalog={enrollments.catalog}
          implementation={bucketData?.implementation}
          specialistName={specialistName}
          apiBase={API}
          token={token}
          onUpdated={(patch) => setContact((c) => ({ ...c, ...patch }))}
        />

        <div className="card" style={{ padding: 0, borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
          <div data-testid="customer-tab-bar" style={{ display: "flex", borderBottom: "0.5px solid var(--border-soft)", padding: "0 8px" }}>
            {TABS.map(t => {
              const active = activeTab === t.id;
              const count = t.id === "deals" ? leads.length : null;
              return (
                <button
                  key={t.id}
                  data-testid={`customer-tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  style={{
                    padding: "12px 16px", fontSize: 12,
                    fontWeight: active ? 600 : 500,
                    background: "transparent", border: "none", cursor: "pointer",
                    color: active ? "var(--blue)" : "var(--text-3)",
                    borderBottom: `2px solid ${active ? "var(--blue)" : "transparent"}`,
                    marginBottom: "-1px",
                    fontFamily: "inherit",
                  }}>
                  {t.label}{count != null && ` (${count})`}
                </button>
              );
            })}
          </div>
          <div data-testid={`customer-tab-content-${activeTab}`} style={{ padding: 18 }}>{renderTab()}</div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <CustomerAIUpsellPanel
            contact={contact}
            currentProducts={currentProducts}
            leadContext={leads[0] ? { specialty: leads[0].specialty, pain_points: painPoints, budget_range: leads[0].budget_range } : null}
            apiBase={API}
            token={token}
          />
          <LeadMeetingsWidget contactId={contact.id} />
        </div>
      </div>

      <QuickNoteModal
        open={quickNoteOpen}
        customerName={contact.company || `${contact.first_name || ""} ${contact.last_name || ""}`.trim()}
        onClose={() => setQuickNoteOpen(false)}
        onSubmit={(text) => {
          if (!text.trim()) return;
          const nextNotes = notes ? `${notes}\n\n[${new Date().toLocaleString()}] ${text.trim()}` : `[${new Date().toLocaleString()}] ${text.trim()}`;
          setNotes(nextNotes);
          axios.put(`${API}/contacts/${contactId}`, { notes: nextNotes }, { headers: hdrs })
            .then(() => toast.success("Note added"))
            .catch((e) => toast.error(_pickErrorMessage(e, "Couldn't save note")));
          setQuickNoteOpen(false);
        }}
      />

      <AddEnrollmentModal
        open={addEnrollmentOpen}
        contactId={contact.id}
        catalog={enrollments.catalog || {}}
        currentProducts={(enrollments.enrollments || []).map(e => e.product)}
        apiBase={API}
        token={token}
        onClose={() => setAddEnrollmentOpen(false)}
        onCreated={() => { loadAll(); }}
      />

      <ChurnExitFormModal
        open={churnModalOpen}
        onClose={() => setChurnModalOpen(false)}
        customerId={contact.id}
        customerName={contact.company || `${contact.first_name || ""} ${contact.last_name || ""}`.trim()}
        apiBase={API}
        token={token}
        defaultRevenue={Number(enrollments?.active_total_monthly || 0)}
        onSubmitted={() => { loadAll(); }}
      />

      <style>{`
        @media (max-width: 1320px) {
          .customer-focus-rails { grid-template-columns: 300px 1fr !important; }
          .customer-focus-rails > *:last-child { grid-column: 1 / -1; display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 16px !important; }
        }
        @media (max-width: 960px) {
          .customer-focus-rails { grid-template-columns: 1fr !important; }
          .customer-focus-rails > *:last-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
